export const THEME_PREFERENCE_STORAGE_KEY = "superplane-theme";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === "light" || value === "dark" || value === "system";
}

export function readStoredThemePreference(): ThemePreference {
  if (typeof window === "undefined") {
    return "system";
  }

  try {
    const stored = window.localStorage.getItem(THEME_PREFERENCE_STORAGE_KEY);
    return isThemePreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

export function resolveTheme(preference: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (preference === "system") {
    return prefersDark ? "dark" : "light";
  }

  return preference;
}

export function getSystemPrefersDark(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

const THEME_COLOR_BY_RESOLVED_THEME: Record<ResolvedTheme, string> = {
  light: "#ffffff",
  dark: "#14120b",
};

export function applyResolvedThemeToDocument(resolvedTheme: ResolvedTheme): void {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.classList.toggle("dark", resolvedTheme === "dark");
  document.documentElement.style.colorScheme = resolvedTheme;
  applyThemeColor(resolvedTheme);
}

function applyThemeColor(resolvedTheme: ResolvedTheme): void {
  const color = THEME_COLOR_BY_RESOLVED_THEME[resolvedTheme];
  const metas = document.head.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  const meta = metas[0] ?? document.createElement("meta");

  if (!meta.isConnected) {
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", color);
  meta.removeAttribute("media");

  for (const extra of Array.from(metas).slice(1)) {
    extra.remove();
  }
}

export function persistThemePreference(preference: ThemePreference): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(THEME_PREFERENCE_STORAGE_KEY, preference);
  } catch {
    // Ignore storage failures (private mode, quota, etc.).
  }
}
